/**
 * useAutoshrinkText — keep a single-line text element on one line by
 * progressively reducing its font-size until it stops overflowing.
 *
 * Why: the KPI card promises that the title, value, context line and
 * reference label all stay readable on a single row at any card width.
 * Truncation (`text-overflow: ellipsis`) hides information — for a
 * value-bearing surface like a market KPI that's actively misleading,
 * so we shrink instead.
 *
 * Usage:
 *   const titleEl = ref(null);
 *   useAutoshrinkText({
 *     el: titleEl,                 // element to scale
 *     min: 9, max: 12,             // px bounds
 *     watchSources: [() => label], // re-fit on content change
 *   });
 *
 * For elements whose width is dictated by a flex/grid parent (e.g. the
 * value+unit row), pass `container` so the overflow check measures the
 * row, not the inner span.
 */
import { isRef, onBeforeUnmount, onMounted, unref, watch } from 'vue';

export function useAutoshrinkText({
  el,
  container = null,
  min = 9,
  max = 14,
  step = 0.5,
  watchSources = [],
} = {}) {
  let observer = null;
  // Tolerance to avoid jitter on sub-pixel rounding — without this the
  // observer can ping-pong between sizes that are 0.1 px from fitting.
  const TOLERANCE = 0.5;

  function fit() {
    const target = el?.value;
    if (!target) return;
    const cont = (container && container.value) || target;
    const minV = unref(min);
    const maxV = unref(max);
    const stepV = unref(step);
    // Reset to max first so a wider card can grow the text back up.
    target.style.fontSize = `${maxV}px`;
    if (cont.scrollWidth <= cont.clientWidth + TOLERANCE) return;
    let size = maxV;
    let guard = 0;
    while (
      size > minV &&
      cont.scrollWidth > cont.clientWidth + TOLERANCE &&
      guard < 200
    ) {
      size = Math.max(minV, size - stepV);
      target.style.fontSize = `${size}px`;
      guard += 1;
    }
  }

  onMounted(() => {
    const target = el?.value;
    if (!target) return;
    const cont = (container && container.value) || target;
    observer = new ResizeObserver(() => fit());
    observer.observe(cont);
    fit();
  });

  onBeforeUnmount(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  });

  // Auto-refit when min/max/step change too — the card swaps between
  // density variants by mutating those refs, and we want the title to
  // resize without a manual call.
  const refOnly = (v) => (isRef(v) ? v : null);
  const reactiveBounds = [refOnly(min), refOnly(max), refOnly(step)].filter(Boolean);

  if (watchSources.length || reactiveBounds.length) {
    // Re-fit after Vue has flushed the new text into the DOM.
    watch(
      [...watchSources, ...reactiveBounds],
      () => Promise.resolve().then(fit),
      { flush: 'post' },
    );
  }

  return { fit };
}
